/**
 * One purchase request's quotations, what the rule needs, and the way round it.
 *
 * The banner at the top is the server's own answer to "can this be submitted"
 * — the same check the submit button runs — so what it says is never a guess
 * made in the browser.
 */

import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import Attachments from './Attachments'
import { Card, ErrorNote, Field, Note, Pill } from './ui'

export interface RequisitionRow {
  id: string
  reference: string
  purpose: string
  needed_by: string | null
  status: string
  estimated_total_paise: number
  requested_by: string
  approval_state: string
  department_id: string | null
  location_id: string | null
  category_id: string | null
  justification: string | null
  purchase_mode: 'via_po' | 'direct'
  selected_total_paise: number | null
  quotation_count: number
  payment_request_id: string | null
  payment_request_reference: string | null
}

interface Quotation {
  id: string
  party_id: string
  party_name: string
  party_status: string
  quotation_no: string | null
  quotation_date: string
  valid_until: string | null
  taxable_paise: number
  tax_paise: number
  total_paise: number
  delivery_days: number | null
  payment_terms: string | null
  is_selected: boolean
  selection_reason: string | null
  withdrawn: boolean
  is_lowest: boolean
  document_count: number
}

interface Requirement {
  required: number
  counted: number
  met: boolean
  problems: string[]
  lowest_quotation_id: string | null
  selected_quotation_id: string | null
  exception_id: string | null
  exception_status: string | null
  exception_reason: string | null
  exception_explanation: string | null
  exception_quotes_available: number | null
  exception_approval_state: string | null
}

interface Option {
  id: string
  code: string | null
  name: string
  note: string | null
}

const REASONS: Record<string, string> = {
  sole_source: 'Only one supplier exists',
  oem_authorised: 'OEM-authorised dealer only',
  proprietary: 'Proprietary item or spare',
  emergency: 'Emergency — no time to collect quotations',
  regulatory: 'Regulatory or licensing requirement',
  other: 'Other',
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  return clean ? Math.round(Number(clean) * 100) : 0
}

const today = () => new Date().toISOString().slice(0, 10)

export default function QuotationsPanel({
  requisition,
  vendors,
  onClose,
  onDone,
}: {
  requisition: RequisitionRow
  vendors: Option[]
  onClose: () => void
  onDone: () => void
}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const draft = requisition.status === 'draft'
  const editable = draft && can('proc.quotation.edit')

  const quotes = useQuery({
    queryKey: ['quotations', requisition.id],
    queryFn: () =>
      api.get<Quotation[]>(`/procurement/requisitions/${requisition.id}/quotations`),
  })
  const rule = useQuery({
    queryKey: ['quotation-requirement', requisition.id],
    queryFn: () =>
      api.get<Requirement>(`/procurement/requisitions/${requisition.id}/quotation-requirement`),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['quotations', requisition.id] })
    void queryClient.invalidateQueries({ queryKey: ['quotation-requirement', requisition.id] })
    onDone()
  }

  const [form, setForm] = useState({
    party_id: '',
    quotation_no: '',
    quotation_date: today(),
    valid_until: '',
    taxable: '',
    tax: '',
    delivery_days: '',
    payment_terms: '',
  })
  const add = useMutation({
    mutationFn: () =>
      api.post(`/procurement/requisitions/${requisition.id}/quotations`, {
        party_id: form.party_id,
        quotation_no: form.quotation_no || null,
        quotation_date: form.quotation_date,
        valid_until: form.valid_until || null,
        taxable_paise: toPaise(form.taxable),
        tax_paise: toPaise(form.tax),
        delivery_days: form.delivery_days ? Number(form.delivery_days) : null,
        payment_terms: form.payment_terms || null,
      }),
    onSuccess: () => {
      setForm({ ...form, party_id: '', quotation_no: '', taxable: '', tax: '', delivery_days: '' })
      refresh()
    },
  })
  const select = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api.post(`/procurement/quotations/${id}/select`, { reason }),
    onSuccess: refresh,
  })
  const withdraw = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/procurement/quotations/${id}/withdraw`, { reason }),
    onSuccess: refresh,
  })
  const raisePayment = useMutation({
    mutationFn: () => api.post(`/procurement/requisitions/${requisition.id}/payment-request`),
    onSuccess: refresh,
  })

  const [openDocs, setOpenDocs] = useState<string | null>(null)
  const rows = quotes.data ?? []
  const requirement = rule.data

  return (
    <Card
      title={`${requisition.reference} — quotations`}
      hint={requisition.purchase_mode === 'direct' ? 'direct purchase' : 'through a purchase order'}
      actions={
        <button className="small" onClick={onClose}>
          Close
        </button>
      }
    >
      <ErrorNote
        error={
          quotes.error ?? rule.error ?? select.error ?? withdraw.error ?? raisePayment.error
        }
      />

      {requirement && (
        <Note kind={requirement.met ? 'ok' : 'warn'}>
          {requirement.required > 0 ? (
            <>
              At this amount the approval rule asks for <b>{requirement.required}</b> quotations
              from different vendors, each with its document attached and still valid.{' '}
              <b>{requirement.counted}</b> count so far.
            </>
          ) : (
            <>No minimum number of quotations applies at this amount.</>
          )}
          {requirement.problems.length > 0 ? (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {requirement.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : (
            draft && <div style={{ marginTop: 4 }}>Ready to submit.</div>
          )}
        </Note>
      )}

      {rows.length === 0 ? (
        <div className="empty">No quotations yet.</div>
      ) : (
        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Quoted</th>
              <th>Valid until</th>
              <th className="num">Taxable</th>
              <th className="num">GST</th>
              <th className="num">Total</th>
              <th>Delivery</th>
              <th>Document</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <Fragment key={q.id}>
                <tr style={q.withdrawn ? { opacity: 0.5 } : undefined}>
                  <td>
                    {q.party_name}
                    {q.party_status !== 'approved' && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        vendor {q.party_status.replace(/_/g, ' ')}
                      </div>
                    )}
                    {q.quotation_no && (
                      <div className="faint mono" style={{ fontSize: 12 }}>
                        {q.quotation_no}
                      </div>
                    )}
                  </td>
                  <td className="muted date">{q.quotation_date}</td>
                  <td className="muted date">
                    {q.valid_until ?? <span className="faint">—</span>}
                    {q.valid_until && q.valid_until < today() && (
                      <div style={{ fontSize: 12 }}>
                        <Pill value="expired" />
                      </div>
                    )}
                  </td>
                  <td className="num">{formatInr(q.taxable_paise)}</td>
                  <td className="num">{formatInr(q.tax_paise)}</td>
                  <td className="num">
                    <b>{formatInr(q.total_paise)}</b>
                    <div style={{ fontSize: 12 }}>
                      {q.is_lowest && <Pill value="lowest" />} {q.is_selected && <Pill value="chosen" />}
                      {q.withdrawn && <Pill value="withdrawn" />}
                    </div>
                  </td>
                  <td className="muted">
                    {q.delivery_days !== null ? `${q.delivery_days} days` : '—'}
                    {q.payment_terms && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {q.payment_terms}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      className="small"
                      onClick={() => setOpenDocs(openDocs === q.id ? null : q.id)}
                    >
                      {q.document_count > 0 ? `${q.document_count} file` : 'Attach'}
                    </button>
                  </td>
                  <td className="num">
                    {editable && !q.withdrawn && !q.is_selected && (
                      <button
                        className="small primary"
                        onClick={() => {
                          let reason: string | null = null
                          if (!q.is_lowest) {
                            reason = window.prompt(
                              'This is not the lowest quotation. Why is it the one to go with?',
                            )
                            if (!reason || !reason.trim()) return
                          }
                          select.mutate({ id: q.id, reason })
                        }}
                      >
                        Choose
                      </button>
                    )}{' '}
                    {editable && !q.withdrawn && (
                      <button
                        className="small"
                        onClick={() => {
                          const reason = window.prompt('Why is this quotation being withdrawn?')
                          if (reason && reason.trim()) withdraw.mutate({ id: q.id, reason })
                        }}
                      >
                        Withdraw
                      </button>
                    )}
                  </td>
                </tr>
                {(openDocs === q.id || (q.is_selected && q.selection_reason)) && (
                  <tr>
                    <td colSpan={9} style={{ background: 'var(--surface-2, #fafafa)' }}>
                      {q.is_selected && q.selection_reason && (
                        <div className="muted" style={{ marginBottom: 6 }}>
                          Chosen because: {q.selection_reason}
                        </div>
                      )}
                      {openDocs === q.id && (
                        <Attachments
                          subjectType="quotation"
                          subjectId={q.id}
                          kind="quotation"
                          canAttach={draft && !q.withdrawn}
                          label="Attach the quotation"
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {draft && can('proc.quotation.create') && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            add.mutate()
          }}
        >
          <h3 style={{ marginBottom: 4 }}>Add a quotation</h3>
          <ErrorNote error={add.error} />
          <div className="row">
            <Field label="Vendor" caption="approved vendors">
              <select
                value={form.party_id}
                onChange={(e) => setForm({ ...form, party_id: e.target.value })}
                required
              >
                <option value="">Choose…</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Quotation no.">
              <input
                value={form.quotation_no}
                onChange={(e) => setForm({ ...form, quotation_no: e.target.value })}
              />
            </Field>
            <Field label="Dated">
              <input
                type="date"
                value={form.quotation_date}
                onChange={(e) => setForm({ ...form, quotation_date: e.target.value })}
                required
              />
            </Field>
            <Field label="Valid until">
              <input
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </Field>
          </div>
          <div className="row">
            <Field label="Taxable (₹)">
              <input
                className="num"
                value={form.taxable}
                onChange={(e) => setForm({ ...form, taxable: e.target.value })}
                required
              />
            </Field>
            <Field label="GST (₹)">
              <input
                className="num"
                value={form.tax}
                onChange={(e) => setForm({ ...form, tax: e.target.value })}
                placeholder="0.00"
              />
            </Field>
            <Field label="Delivery (days)">
              <input
                className="num"
                value={form.delivery_days}
                onChange={(e) => setForm({ ...form, delivery_days: e.target.value })}
              />
            </Field>
            <Field label="Payment terms">
              <input
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                placeholder="50% advance, balance on delivery"
              />
            </Field>
            <div className="shrink">
              <button className="primary" type="submit" disabled={add.isPending}>
                Add
              </button>
            </div>
          </div>
          <Note kind="info">
            After adding it, attach the quotation document — a quotation without its document
            does not count.
          </Note>
        </form>
      )}

      {requirement && (requirement.required > 0 || requirement.exception_id) && (
        <ExceptionSection
          requisition={requisition}
          requirement={requirement}
          onDone={refresh}
        />
      )}

      {requisition.purchase_mode === 'direct' && requisition.status === 'approved' && (
        <div style={{ marginTop: 12 }}>
          {can('spend.payment_request.create') && !requisition.payment_request_id && (
            <button
              className="primary"
              disabled={raisePayment.isPending}
              onClick={() => raisePayment.mutate()}
            >
              Raise payment request for {formatInr(requisition.selected_total_paise ?? 0)}
            </button>
          )}
        </div>
      )}
      {requisition.payment_request_reference && (
        <Note kind="info">
          Paid through payment request{' '}
          <Link to="/spending/payment-requests">{requisition.payment_request_reference}</Link>,
          which carries this request's approval for the chosen vendor at the chosen price.
        </Note>
      )}
    </Card>
  )
}

function ExceptionSection({
  requisition,
  requirement,
  onDone,
}: {
  requisition: RequisitionRow
  requirement: Requirement
  onDone: () => void
}) {
  const { can } = useAuth()
  const draft = requisition.status === 'draft'
  const status = requirement.exception_status
  const [form, setForm] = useState({
    reason_code: requirement.exception_reason ?? 'sole_source',
    quotes_available: requirement.exception_quotes_available ?? requirement.counted,
    explanation: requirement.exception_explanation ?? '',
  })
  const [open, setOpen] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      api.post(`/procurement/requisitions/${requisition.id}/quotation-exception`, form),
    onSuccess: () => {
      setOpen(false)
      onDone()
    },
  })
  const submit = useMutation({
    mutationFn: () =>
      api.post(`/procurement/quotation-exceptions/${requirement.exception_id}/submit`),
    onSuccess: onDone,
  })

  const editable =
    draft && can('proc.quotation_exception.create') && (!status || status === 'draft' || status === 'rejected')

  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 4 }}>Quotation exception</h3>
      <ErrorNote error={save.error ?? submit.error} />
      {status ? (
        <div style={{ marginBottom: 8 }}>
          <Pill value={status} />{' '}
          <span className="muted">
            {REASONS[requirement.exception_reason ?? ''] ?? requirement.exception_reason} —{' '}
            {requirement.exception_quotes_available} available. {requirement.exception_explanation}
          </span>
          {status === 'submitted' && (
            <div className="faint" style={{ fontSize: 12 }}>
              {requirement.exception_approval_state}
            </div>
          )}
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 0 }}>
          When fewer quotations exist than the rule asks for, say why and attach the proof. The
          Finance Head decides it before the request can go.
        </p>
      )}

      {requirement.exception_id && (status === 'draft' || status === 'submitted' || status === 'approved') && (
        <div style={{ marginBottom: 8 }}>
          <div className="caption">Evidence</div>
          <Attachments
            subjectType="quotation_exception"
            subjectId={requirement.exception_id}
            kind="exception_evidence"
            canAttach={status === 'draft'}
            label="Attach evidence"
          />
        </div>
      )}

      {status === 'draft' && can('proc.quotation_exception.submit') && (
        <button className="primary small" disabled={submit.isPending} onClick={() => submit.mutate()}>
          Send exception for approval
        </button>
      )}{' '}
      {editable && !open && (
        <button className="small" onClick={() => setOpen(true)}>
          {status ? 'Rewrite the exception' : 'Request an exception'}
        </button>
      )}

      {editable && open && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <div className="row">
            <Field label="Reason">
              <select
                value={form.reason_code}
                onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
              >
                {Object.entries(REASONS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Quotations available">
              <input
                type="number"
                min={0}
                max={5}
                value={form.quotes_available}
                onChange={(e) => setForm({ ...form, quotes_available: Number(e.target.value) })}
              />
            </Field>
          </div>
          <Field label="Explanation" caption="at least 30 characters — who the suppliers are, why no others">
            <textarea
              rows={3}
              value={form.explanation}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              required
            />
          </Field>
          <div className="row">
            <div className="shrink">
              <button className="primary" type="submit" disabled={save.isPending}>
                Save exception
              </button>{' '}
              <button type="button" className="small" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}
